import React, { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAccountHierarchy, useCostCentresMy, useMyAccounts } from "../../account/hooks";
import { AccountHierarchy, AccountInfo, AccountLevels } from "../../account/types";
import { useEnabledFeatures } from "../../feature/hooks";
import { useMyPermissions } from "../../permission/hooks";
import { Modify } from "../../_app/utils/types";
import { AccountLevel } from "../../account/utils";
import { getContextIdFromStorage, setContextIdToStorage } from "../utils";
import { useMyUserLevelType } from "../../user-level/hooks";
import { UserType } from "../../user-level/types";

interface ContextValue {
  // id for account, subaccount or cost centre
  contextId: string;
  contextData?: Modify<AccountInfo, { level?: AccountLevel }>;
  setContextId: React.Dispatch<React.SetStateAction<string>>;

  hierarchy?: AccountHierarchy["hierarchy"];
  findLevelInHierarchy: (level: AccountLevels) => AccountInfo | undefined;

  selectedGroupId?: string;
  setSelectedGroupId: React.Dispatch<React.SetStateAction<string | undefined>>;

  userType?: UserType;
  reset: () => void;

  isReady: boolean;
  isReadyForGroupSelection: boolean;
}

const Context = createContext<ContextValue | undefined>(undefined);

export const useContextProvider = () => {
  const context = useContext(Context);
  if (!context) {
    throw new Error("useContextProvider must be used within ContextProvider");
  }
  return context;
};

export const ContextProvider = ({ children }: PropsWithChildren) => {
  const [contextId, setContextId] = useState<string>("");
  const [selectedGroupId, setSelectedGroupId] = useState<string | undefined>();
  const [isCalculating, setIsCalculating] = useState<boolean>(true);
  const hasSetPersistedContext = useRef(false);
  const { data: userType } = useMyUserLevelType();
  const { data: accountHierarchy } = useAccountHierarchy(contextId);
  const { data: groups, isSuccess: groupsFetched } = useMyAccounts();
  const { data: costCentreData } = useCostCentresMy();

  // Cost centre context id - only set if no context has been restored yet
  useEffect(
    function setCostCentreContextId() {
      const id = costCentreData?.costCentreId;
      if (id && !hasSetPersistedContext.current && !contextId) {
        setContextId(id);
      }
    },
    [costCentreData, contextId],
  );

  // When contextId is set, persist it to storage
  useEffect(
    function persistContextId() {
      if (contextId) {
        setContextIdToStorage(contextId);
      }
    },
    [contextId],
  );

  // Clear contextId when selectedGroupId changes
  useEffect(
    function resetContextIdOnGroupChange() {
      if (selectedGroupId) setContextId("");
    },
    [selectedGroupId],
  );

  const accessibleAccountIds = useMemo(() => {
    return groups?.flatMap((group) => group.userAccessibleAccounts?.map((account) => account.id) || []) || [];
  }, [groups]);

  const tryRestorePersistedContext = useCallback(() => {
    if (!hasSetPersistedContext.current && !selectedGroupId) {
      const persistedContextId = getContextIdFromStorage();

      if (persistedContextId && accessibleAccountIds.includes(persistedContextId)) {
        setContextId(persistedContextId);
        hasSetPersistedContext.current = true;
        return true;
      }
      hasSetPersistedContext.current = true;
    }
    return false;
  }, [accessibleAccountIds, selectedGroupId]);

  const selectAccountFromGroups = useCallback(() => {
    if (!groups) return;

    const groupIndex = groups.length === 1 ? 0 : groups.findIndex((group) => group.id === selectedGroupId);
    const firstAccountId = groupIndex >= 0 ? groups[groupIndex]?.userAccessibleAccounts?.[0]?.id : undefined;

    if (firstAccountId) {
      setContextId(firstAccountId);
    } else if (!selectedGroupId) {
      setIsCalculating(false);
    }
  }, [groups, selectedGroupId]);

  useEffect(() => {
    if (groupsFetched) {
      // If we have no contextId, try to set one
      if (!contextId) {
        if (tryRestorePersistedContext()) return;
        selectAccountFromGroups();
      }
    }
  }, [contextId, groupsFetched, selectedGroupId, tryRestorePersistedContext, selectAccountFromGroups]);

  // Centralized isCalculating state management
  useEffect(() => {
    if (contextId) {
      setIsCalculating(false);
    }
  }, [contextId]);

  // Construct context data
  const contextData = useMemo(() => {
    const match = Object.values(accountHierarchy?.hierarchy || {}).find((account) => account.id === contextId);
    if (match) {
      return {
        ...match,
        level: accountHierarchy?.level,
      };
    }
  }, [accountHierarchy, contextId]);

  // Fetch permissions and features — gate readiness on the initial load only;
  // background refetches must not flip the whole app back to "not ready"
  const { isLoading: isPermissionsFetching } = useMyPermissions(contextId);
  const { isLoading: isFeatureFetching } = useEnabledFeatures({ enabled: Boolean(contextId) });

  const reset = useCallback(() => {
    setContextId("");
    setIsCalculating(true);
    setSelectedGroupId(undefined);
    hasSetPersistedContext.current = false;
  }, []);

  // Calculate if context is ready
  const isPseudoReady = useMemo(() => {
    return !isPermissionsFetching && !isFeatureFetching;
  }, [isPermissionsFetching, isFeatureFetching]);

  const isReadyForGroupSelection = useMemo(() => {
    return isPseudoReady && !isCalculating && !contextId && !selectedGroupId;
  }, [isPseudoReady, isCalculating, contextId, selectedGroupId]);

  const isReady = useMemo(() => {
    return Boolean(isPseudoReady && contextId);
  }, [isPseudoReady, contextId]);

  const findLevelInHierarchy = useCallback(
    (level: AccountLevels) => {
      return accountHierarchy?.hierarchy[level];
    },
    [accountHierarchy?.hierarchy],
  );

  const computedSelectedGroupId = useMemo(
    () => selectedGroupId ?? accountHierarchy?.hierarchy?.GROUP?.id,
    [selectedGroupId, accountHierarchy?.hierarchy?.GROUP?.id],
  );

  const contextValue = useMemo(
    () => ({
      contextId,
      contextData,
      setContextId,

      hierarchy: accountHierarchy?.hierarchy,
      findLevelInHierarchy,

      selectedGroupId: computedSelectedGroupId,
      setSelectedGroupId,

      userType,
      reset,
      isReady,
      isReadyForGroupSelection,
    }),
    [
      reset,
      contextId,
      contextData,
      accountHierarchy?.hierarchy,
      findLevelInHierarchy,
      computedSelectedGroupId,
      userType,
      isReady,
      isReadyForGroupSelection,
    ],
  );

  return <Context.Provider value={contextValue}>{children}</Context.Provider>;
};
